import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownIcon, LayoutGrid, Tag } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel, Switch } from "@headlessui/react";
import { fromJS } from "immutable";
import GroupBy from "./GroupBy";
import { BOARD_GROUP_BY } from "../../../constants";
import TaskAction from "../../../actions/TaskAction";
import { SortType } from "../sort/SortBy";
import VisibleColumn from "./VisibleColumn";
import CardRowSettings from "./CardRowSettings";
import BoardSortSettings from "./BoardSortSettings";
import GridColumnSettings from "./GridColumnSettings";

interface ViewSettingsProps {
  contentType: string;
  projectView: string;
  sorts: any;
  onChangeSort: (sorts: SortType[]) => void;
}

const ViewSettings = ({ contentType, projectView, sorts, onChangeSort }: ViewSettingsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const groupBy = useSelector((state: any) => state.taskReducer.get("groupBy"));
  const groupByVisibleFieldIds =
    useSelector((state: any) => state.taskReducer.get("groupByVisibleFieldIds")) || fromJS([]);
  const showCardLabels = useSelector((state: any) => state.taskReducer.get("showCardLabels"));

  const onChangeGroup = (groupKey: string) => {
    dispatch(TaskAction.changeGroupBy(groupKey, groupByVisibleFieldIds.toJS()));
  };

  const onChangeVisibleColumn = (columnIds: string[]) => {
    dispatch(TaskAction.changeGroupBy(groupBy, columnIds));
  };

  const renderSetting = (close) => {
    if (projectView === "BOARD") {
      const groupByMenus = BOARD_GROUP_BY(t, contentType);
      return (
        <>
          <GroupBy
            menus={groupByMenus}
            selected={groupBy}
            onChange={onChangeGroup}
            onClose={close}
          />
          <VisibleColumn contentType={contentType} onChange={onChangeVisibleColumn} />
          <BoardSortSettings sorts={sorts} onChangeSort={onChangeSort} />
          <div className="flex items-center justify-between space-x-2 p-2">
            <div className="flex items-center space-x-2">
              <Tag className="w-5 h-5" />
              <span className="text-nowrap text-sm font-medium">{t("Show labels")}</span>
            </div>
            <Switch
              checked={showCardLabels}
              onChange={() => dispatch(TaskAction.changeShowCardLabels(!showCardLabels))}
              className={`${
                showCardLabels ? "bg-blue-600" : "bg-gray-200"
              } relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`}
            >
              <span
                aria-hidden="true"
                className={`${
                  showCardLabels ? "translate-x-4" : "translate-x-0"
                } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
          </div>
          <hr className="border-gray-200" />
          <CardRowSettings contentType={contentType} />
        </>
      );
    }
    if (projectView === "GRID") {
      return (
        <>
          <GridColumnSettings contentType={contentType} />
        </>
      );
    }
    return null;
  };

  if (projectView !== "BOARD" && projectView !== "GRID") return null;

  return (
    <div>
      <Popover>
        <PopoverButton className="inline-flex items-center space-x-2 rounded-md h-8 px-2 py-1.5 text-sm font-medium text-gray-500 border border-gray-300 outline-none">
          <LayoutGrid className="h-4 w-4 text-gray-500" />
          <div className="pr-2">{t("View Settings")}</div>
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        </PopoverButton>
        <PopoverPanel
          anchor="bottom start"
          className="z-20 mt-2 p-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
        >
          {({ close }) => (
            <div className={"min-w-60 space-y-2"}>
              <div className="text-xs font-medium text-gray-500">{t("Settings")}</div>
              {renderSetting(close)}
            </div>
          )}
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default ViewSettings;
